import { useEffect, useState, ReactNode } from 'react';
import { tennisStats, basketballStats } from '@/mocks/estadisticas';
import type { RealStanding } from '@/pages/estadisticas/components/FootballTable';

type TennisPlayer = typeof tennisStats[0];
type BasketballTeam = typeof basketballStats[0];

export type PanelItem =
  | { sport: 'Fútbol'; data: RealStanding }
  | { sport: 'Tenis'; data: TennisPlayer }
  | { sport: 'Baloncesto'; data: BasketballTeam };

interface DetailPanelProps {
  item: PanelItem | null;
  isOpen: boolean;
  onClose: () => void;
}

function FormBadge({ r }: { r: string }) {
  const cls =
    r === 'W' ? 'bg-[#00e87a]/20 text-[#00e87a]' :
    r === 'L' ? 'bg-[#ef4444]/20 text-[#ef4444]' :
    'bg-[#f59e0b]/20 text-[#f59e0b]';
  return (
    <span className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-black ${cls}`}>{r}</span>
  );
}

function StatRow({ label, value, color, suffix }: { label: string; value: string | number; color?: string; suffix?: string }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-[#6b7280] text-xs">{label}</span>
      <span className="font-bold text-sm tabular-nums" style={{ color: color ?? '#ffffff' }}>
        {value}{suffix}
      </span>
    </div>
  );
}

function StatGrid({ items }: { items: { label: string; value: string | number; color?: string }[] }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {items.map((item) => (
        <div key={item.label} className="bg-[#151719] rounded-lg p-2.5 text-center">
          <p className="text-[#6b7280] text-xs mb-1">{item.label}</p>
          <p className="font-black text-sm" style={{ color: item.color ?? '#ffffff' }}>{item.value}</p>
        </div>
      ))}
    </div>
  );
}

function Section({ title, icon, children }: { title: string; icon: string; children: ReactNode }) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-4 h-4 flex items-center justify-center text-[#00e87a]">
          <i className={`${icon} text-sm`}></i>
        </div>
        <p className="text-[#6b7280] text-xs font-bold uppercase tracking-widest">{title}</p>
      </div>
      {children}
    </div>
  );
}

function FootballDetail({ team }: { team: RealStanding }) {
  const goalsForAvg = team.played > 0 ? (team.goalsFor / team.played).toFixed(2) : '0';
  const goalsAgainstAvg = team.played > 0 ? (team.goalsAgainst / team.played).toFixed(2) : '0';
  return (
    <>
      {/* Big hero stats */}
      <div className="grid grid-cols-3 gap-2 mb-5">
        {[
          { label: 'Pts', value: team.points, color: '#ffd700' },
          { label: 'Goles/P', value: goalsForAvg, color: '#00e87a' },
          { label: 'DG', value: team.goalDiff > 0 ? `+${team.goalDiff}` : String(team.goalDiff), color: team.goalDiff >= 0 ? '#00e87a' : '#ef4444' },
        ].map((s) => (
          <div key={s.label} className="bg-[#0d0f14] rounded-xl p-3 text-center">
            <p style={{ color: s.color }} className="font-black text-xl">{s.value}</p>
            <p className="text-[#6b7280] text-xs mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      <Section title="Clasificación" icon="ri-trophy-line">
        <div className="bg-[#0d0f14] rounded-xl p-3 space-y-1.5">
          <StatRow label="Posición" value={`#${team.position}`} color="#ffd700" />
          <StatRow label="Partidos jugados" value={team.played} />
          <StatRow label="Puntos" value={team.points} color="#ffd700" />
        </div>
      </Section>

      <Section title="Récord" icon="ri-bar-chart-2-line">
        <div className="bg-[#0d0f14] rounded-xl p-3 space-y-1.5">
          <StatRow label="Victorias" value={team.won} color="#00e87a" />
          <StatRow label="Empates" value={team.draw} color="#f59e0b" />
          <StatRow label="Derrotas" value={team.lost} color="#ef4444" />
        </div>
      </Section>

      <Section title="Goles" icon="ri-football-line">
        <div className="bg-[#0d0f14] rounded-xl p-3 space-y-1.5">
          <StatRow label="Goles a favor" value={team.goalsFor} color={team.goalsFor > 40 ? '#00e87a' : '#ffffff'} />
          <StatRow label="Goles en contra" value={team.goalsAgainst} color="#6b7280" />
          <StatRow label="Diferencia" value={team.goalDiff > 0 ? `+${team.goalDiff}` : String(team.goalDiff)} color={team.goalDiff >= 0 ? '#00e87a' : '#ef4444'} />
          <StatRow label="Media goles/P" value={goalsForAvg} color="#a8b2c1" />
          <StatRow label="Media recibidos/P" value={goalsAgainstAvg} color="#6b7280" />
        </div>
      </Section>

      <Section title="Stats Avanzadas" icon="ri-lock-line">
        <div className="bg-[#0d0f14] rounded-xl p-4 text-center border border-[#ffd700]/20">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#ffd700]/15 mx-auto mb-2">
            <i className="ri-vip-crown-line text-[#ffd700] text-sm"></i>
          </div>
          <p className="text-[#ffd700] font-bold text-xs mb-1">Disponible con Premium</p>
          <p className="text-[#6b7280] text-xs">xG · Corners · BTTS · Over 2.5 · Posesión</p>
        </div>
      </Section>
    </>
  );
}

function TennisDetail({ player }: { player: TennisPlayer }) {
  return (
    <>
      {/* Big hero stats */}
      <div className="grid grid-cols-3 gap-2 mb-5">
        {[
          { label: 'Win%', value: `${player.winRate}%`, color: player.winRate >= 80 ? '#00e87a' : '#f59e0b' },
          { label: 'Sets W%', value: `${player.setsWon}%`, color: '#ffd700' },
          { label: 'Partidos', value: player.played, color: '#a8b2c1' },
        ].map((s) => (
          <div key={s.label} className="bg-[#0d0f14] rounded-xl p-3 text-center">
            <p style={{ color: s.color }} className="font-black text-xl">{s.value}</p>
            <p className="text-[#6b7280] text-xs mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      <Section title="Servicio" icon="ri-navigation-line">
        <div className="bg-[#0d0f14] rounded-xl p-3 space-y-1.5">
          <StatRow label="1st Srv Win%" value={`${player.firstServeWin}%`} color={player.firstServeWin > 75 ? '#00e87a' : '#ffffff'} />
          <StatRow label="Aces / partido" value={player.acesPerMatch} color={player.acesPerMatch > 8 ? '#00e87a' : '#ffffff'} />
          <StatRow label="Dobles faltas / P" value={player.dfPerMatch} color={player.dfPerMatch > 2.5 ? '#ff6b35' : '#a8b2c1'} />
        </div>
      </Section>

      <Section title="Break Points" icon="ri-flashlight-line">
        <StatGrid items={[
          { label: 'BP Saved%', value: `${player.bpSaved}%`, color: player.bpSaved >= 70 ? '#00e87a' : player.bpSaved >= 60 ? '#f59e0b' : '#ffffff' },
          { label: 'BP Conv%', value: `${player.bpConverted}%`, color: player.bpConverted >= 50 ? '#00e87a' : player.bpConverted >= 43 ? '#f59e0b' : '#ffffff' },
        ]} />
      </Section>

      <Section title="Rendimiento general" icon="ri-bar-chart-line">
        <div className="bg-[#0d0f14] rounded-xl p-3 space-y-1.5">
          <StatRow label="Sets ganados%" value={`${player.setsWon}%`} color={player.setsWon > 88 ? '#00e87a' : '#ffffff'} />
          <StatRow label="Ranking" value={`#${player.ranking}`} color="#ffd700" />
          <StatRow label="Circuito" value={player.tour} color="#a8b2c1" />
        </div>
      </Section>
    </>
  );
}

function BasketballDetail({ team }: { team: BasketballTeam }) {
  return (
    <>
      {/* Big hero stats */}
      <div className="grid grid-cols-3 gap-2 mb-5">
        {[
          { label: 'Pts/P', value: team.ptsFor, color: team.ptsFor >= 118 ? '#00e87a' : '#ffffff' },
          { label: 'FG%', value: `${team.fgPct}%`, color: team.fgPct >= 50 ? '#00e87a' : '#ffd700' },
          { label: '+/-', value: team.plusMinus > 0 ? `+${team.plusMinus}` : team.plusMinus, color: team.plusMinus > 0 ? '#00e87a' : '#ef4444' },
        ].map((s) => (
          <div key={s.label} className="bg-[#0d0f14] rounded-xl p-3 text-center">
            <p style={{ color: s.color }} className="font-black text-xl">{s.value}</p>
            <p className="text-[#6b7280] text-xs mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      <Section title="Anotación" icon="ri-numbers-line">
        <div className="bg-[#0d0f14] rounded-xl p-3 space-y-1.5">
          <StatRow label="Puntos/P" value={team.ptsFor} color={team.ptsFor >= 118 ? '#00e87a' : '#ffffff'} />
          <StatRow label="Puntos rival/P" value={team.ptsAgainst} color={team.ptsAgainst < 108 ? '#00e87a' : '#6b7280'} />
          <StatRow label="Diferencial" value={team.plusMinus > 0 ? `+${team.plusMinus}` : team.plusMinus} color={team.plusMinus > 0 ? '#00e87a' : '#ef4444'} />
        </div>
      </Section>

      <Section title="Rebotes" icon="ri-arrow-up-down-line">
        <StatGrid items={[
          { label: 'Total/P', value: team.rebPerGame, color: team.rebPerGame > 43 ? '#00e87a' : '#ffffff' },
          { label: 'Ofensivos', value: team.rebOff, color: '#a8b2c1' },
          { label: 'Defensivos', value: team.rebDef, color: '#a8b2c1' },
          { label: 'Asistencias/P', value: team.astPerGame, color: team.astPerGame > 25 ? '#00e87a' : '#ffffff' },
        ]} />
      </Section>

      <Section title="Eficiencia ofensiva" icon="ri-percent-line">
        <div className="bg-[#0d0f14] rounded-xl p-3 space-y-1.5">
          <StatRow label="FG%" value={`${team.fgPct}%`} color={team.fgPct >= 50 ? '#00e87a' : team.fgPct >= 47 ? '#f59e0b' : '#ffffff'} />
          <StatRow label="3P%" value={`${team.threePct}%`} color={team.threePct >= 39 ? '#00e87a' : '#a8b2c1'} />
          <StatRow label="Partidos jugados" value={team.played} color="#a8b2c1" />
        </div>
      </Section>
    </>
  );
}

export default function DetailPanel({ item, isOpen, onClose }: DetailPanelProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen && item) {
      const t = setTimeout(() => setVisible(true), 10);
      return () => clearTimeout(t);
    } else {
      setVisible(false);
    }
  }, [isOpen, item]);

  if (!item) return null;

  const name =
    item.sport === 'Tenis'
      ? item.data.player
      : item.sport === 'Fútbol'
      ? item.data.team
      : item.data.team;

  const subtitle =
    item.sport === 'Tenis'
      ? item.data.tour
      : item.sport === 'Fútbol'
      ? item.data.league ?? 'Fútbol'
      : item.data.league;

  const sportIcon =
    item.sport === 'Fútbol' ? 'ri-football-line' :
    item.sport === 'Tenis' ? 'ri-navigation-line' :
    'ri-basketball-line';

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />

      {/* Sliding panel */}
      <div
        className={`fixed right-0 top-0 h-full w-full max-w-[400px] z-50 bg-[#0f1115] border-l border-[#2a2d3a] overflow-y-auto flex flex-col transition-transform duration-300 ${visible ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Panel header */}
        <div className="sticky top-0 z-10 bg-[#0f1115] border-b border-[#2a2d3a] px-5 py-4 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-[#00e87a]/10 text-[#00e87a] flex-shrink-0">
              <i className={`${sportIcon} text-base`}></i>
            </div>
            <div className="min-w-0">
              <p translate="no" className="text-white font-black text-base leading-tight truncate">{name}</p>
              <p translate="no" className="text-[#6b7280] text-xs mt-0.5">{subtitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-[#6b7280] hover:text-white hover:bg-white/10 rounded-lg transition-all cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Form strip */}
        <div className="px-5 py-4 border-b border-[#2a2d3a]">
          <p className="text-[#6b7280] text-xs font-bold uppercase tracking-widest mb-3">Últimos 5 resultados</p>
          <div className="flex items-center gap-2">
            {item.data.form.map((r, i) => <FormBadge key={i} r={r} />)}
            {item.data.form.length === 0 && <span className="text-[#3d4149] text-xs">Sin datos de forma</span>}
            <span className="text-[#6b7280] text-xs ml-auto">
              {item.data.form.filter((r) => r === 'W').length}V –{' '}
              {item.data.form.filter((r) => r === 'L').length}D
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 px-5 py-5">
          {item.sport === 'Fútbol' && <FootballDetail team={item.data as RealStanding} />}
          {item.sport === 'Tenis' && <TennisDetail player={item.data as TennisPlayer} />}
          {item.sport === 'Baloncesto' && <BasketballDetail team={item.data as BasketballTeam} />}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-[#2a2d3a]">
          <button
            onClick={onClose}
            className="w-full py-2.5 border border-[#2a2d3a] text-[#a8b2c1] text-sm font-medium rounded-xl hover:bg-white/5 cursor-pointer transition-all whitespace-nowrap"
          >
            Cerrar ficha
          </button>
        </div>
      </div>
    </>
  );
}
